import { initializeApp } from 'firebase/app';
import {
  getAuth,
  setPersistence,
  browserLocalPersistence,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
} from 'firebase/auth';
import { getFirestore } from 'firebase/firestore';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
};

const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);
export const db = getFirestore(app);

// Раньше persistence не задавался явно — использовался дефолт Firebase (LOCAL/IndexedDB),
// который в большинстве браузеров и так сохраняет сессию между обновлениями страницы.
// Проблема была в том, что до того, как onAuthStateChanged первый раз отработает (это
// асинхронно, требует чтения IndexedDB), экран входа мог на мгновение показаться —
// выглядело как разлогин при каждом обновлении. Плюс: явный setPersistence(LOCAL) —
// это гарантия, а не предположение о поведении браузера по умолчанию.
//
// Важно понимать ограничение, которое НЕЛЬЗЯ починить кодом: если открывать сайт то как
// обычную вкладку Safari, то как «иконку на экране Домой» — iOS считает это ДВУМЯ разными
// изолированными хранилищами. Вход в одном НЕ переносится в другой — это архитектура iOS,
// не баг приложения. Нужно всегда пользоваться одним и тем же способом входа на каждом
// устройстве (или один раз залогиниться в каждом контексте отдельно).
const persistenceReady = setPersistence(auth, browserLocalPersistence);

/**
 * Resolves once we know for certain whether a user is logged in — never earlier.
 * The app's boot sequence must await this before deciding whether to show the
 * login screen or the app, so a slow IndexedDB read never flashes the login gate.
 */
export function waitForAuthReady() {
  return persistenceReady.then(
    () =>
      new Promise((resolve) => {
        const unsub = onAuthStateChanged(auth, (user) => {
          unsub();
          resolve(user);
        });
      })
  );
}

export function subscribeAuth(callback) {
  return onAuthStateChanged(auth, callback);
}

export function login(email, password) {
  return persistenceReady.then(() => signInWithEmailAndPassword(auth, email, password));
}

export function register(email, password) {
  return persistenceReady.then(() => createUserWithEmailAndPassword(auth, email, password));
}

export function logout() {
  return signOut(auth);
}
